import React from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'
import { FormTextInput } from '@divvy-web/skylab.form'
import { useForm } from '@divvy-web/skylab.react-hook-form'

import { useGetCreditApplicationForReview } from '@sputnik/pages/CreditApplicationPages/ReviewForm/GetCreditApplicationForReview.gql'
import SectionCollapsibleList from '@sputnik/pages/CreditApplicationPages/ReviewForm/SectionCollapsibleList'
import {
  hasEmptyValue,
  hasMissingRequiredField,
  toSentenceCase,
} from '@sputnik/pages/CreditApplicationPages/ReviewForm/utils'
import { formatDateForDisplay } from '@sputnik/pages/CreditApplicationPages/ReviewForm/utils'
import { MAJOR_ACCOUNT_CHANGE, PATHNAME_CONSTS } from '@sputnik/resources/constants'
import { useWebTracking } from '@sputnik/tracking/WebTrackingContext'

import styles from './styles.module.scss'

const NameAndOwnershipReviewSection = () => {
  const navigate = useNavigate()
  const { appId } = useParams()
  const { control } = useForm()
  const [getClassName] = useNameFormatter('NameAndOwnershipReviewSection')
  const { tracker } = useWebTracking()

  const { data } = useGetCreditApplicationForReview({
    variables: {
      creditApplicationId: appId ?? '',
    },
  })

  const isMajorAccountChange = data?.creditApplication?.recordType === MAJOR_ACCOUNT_CHANGE

  const {
    legalBusinessName,
    preferredAccountName,
    ownership,
    formedOn,
    taxIdLastFour,
    entityType,
    numberOfEmployees,
    naicsCode,
    website,
    industry,
  } = data?.creditApplication?.businessInfo || {}

  const hasMissingInfo =
    !!data &&
    hasMissingRequiredField([
      legalBusinessName,
      ownership,
      formedOn,
      taxIdLastFour,
      entityType,
      ...(!isMajorAccountChange ? [numberOfEmployees] : []),
      naicsCode,
      industry,
    ])

  const handleOnEditClick = () => {
    tracker?.track('se_signup__review_and_sign_edit_info__fired', {
      application_section: 'Name and ownership',
      navigated_to_page: `${PATHNAME_CONSTS.NAME_AND_OWNERSHIP_PATH}`,
    })
    navigate(`../${PATHNAME_CONSTS.NAME_AND_OWNERSHIP_PATH}`)
  }

  return (
    <div className={`${styles[getClassName('')]} fs-unmask`}>
      <SectionCollapsibleList
        hasMissingInfo={hasMissingInfo}
        headerText={
          <FormattedMessage
            defaultMessage='Name and ownership'
            id='sputnik.NameAndOwnershipReviewSection__RyW8Fx'
          />
        }
        onEditClick={handleOnEditClick}
      >
        <FormTextInput
          readOnly
          control={control}
          isRequired={hasEmptyValue(legalBusinessName)}
          label={
            <span className='fs-unmask'>
              <FormattedMessage
                defaultMessage='Legal business name'
                id='sputnik.NameAndOwnershipReviewSection__U/+zt/'
              />
            </span>
          }
          name='legalBusinessName'
          value={legalBusinessName ?? '-'}
        />
        <FormTextInput
          readOnly
          control={control}
          label={
            <span className='fs-unmask'>
              <FormattedMessage
                defaultMessage='Preferred business name'
                id='sputnik.NameAndOwnershipReviewSection__Hq6+4+'
              />
            </span>
          }
          name='preferredAccountName'
          value={preferredAccountName ?? ''}
        />
        <FormTextInput
          readOnly
          control={control}
          label={
            <span className='fs-unmask'>
              <FormattedMessage
                defaultMessage='Business website'
                id='sputnik.NameAndOwnershipReviewSection__go2n+4'
              />
            </span>
          }
          name='website'
          value={website ?? ''}
        />
        <FormTextInput
          readOnly
          className='fs-mask'
          control={control}
          isRequired={hasEmptyValue(taxIdLastFour)}
          label={
            <span className='fs-unmask'>
              <FormattedMessage
                defaultMessage='Business tax ID / EIN'
                id='sputnik.NameAndOwnershipReviewSection__sgg/zz'
              />
            </span>
          }
          name='taxId'
          value={taxIdLastFour ? `*****${taxIdLastFour}` : '—'}
        />
        <FormTextInput
          readOnly
          control={control}
          isRequired={hasEmptyValue(ownership)}
          label={
            <span className='fs-unmask'>
              <FormattedMessage
                defaultMessage='Ownership'
                id='sputnik.NameAndOwnershipReviewSection__wkn5Ny'
              />
            </span>
          }
          name='ownership'
          value={ownership ? `${ownership?.charAt(0) + ownership.slice(1).toLowerCase()}` : '—'}
        />
        <FormTextInput
          readOnly
          control={control}
          isRequired={hasEmptyValue(formedOn)}
          label={
            <span className='fs-unmask'>
              <FormattedMessage
                defaultMessage='Formation date'
                id='sputnik.NameAndOwnershipReviewSection__MHV8J9'
              />
            </span>
          }
          name='formedOn'
          value={formedOn ? formatDateForDisplay(formedOn) : '—'}
        />
        <FormTextInput
          readOnly
          control={control}
          isRequired={!isMajorAccountChange && hasEmptyValue(numberOfEmployees)}
          label={
            <span className='fs-unmask'>
              <FormattedMessage
                defaultMessage='Number of employees'
                id='sputnik.NameAndOwnershipReviewSection__cErP2u'
              />
            </span>
          }
          name='numberOfEmployees'
          value={numberOfEmployees || '—'}
        />
        <FormTextInput
          readOnly
          control={control}
          isRequired={hasEmptyValue(naicsCode)}
          label={
            <span className='fs-unmask'>
              <FormattedMessage
                defaultMessage='Industry'
                id='sputnik.NameAndOwnershipReviewSection__TqlgrX'
              />
            </span>
          }
          name='industry'
          value={industry || '—'}
        />
        <FormTextInput
          readOnly
          control={control}
          isRequired={hasEmptyValue(entityType)}
          label={
            <span className='fs-unmask'>
              <FormattedMessage
                defaultMessage='Entity type'
                id='sputnik.NameAndOwnershipReviewSection__adhRTA'
              />
            </span>
          }
          name='entityType'
          value={entityType ? toSentenceCase(entityType) : '—'}
        />
      </SectionCollapsibleList>
    </div>
  )
}

export default NameAndOwnershipReviewSection
